/**
 * 单词卡片弹层(跨页面共享).
 *
 * 跟读页点高亮词、我的收藏点单词词条都打开同一个弹层, 因此状态与逻辑放在 store,
 * 组件通过 `$store.wordCard.*` 读写; 这样切页面不丢状态, 也不必复制弹层与收藏/发音逻辑。
 *
 * 收藏成功后派发 `word-favorite-changed` 事件, 供收藏页同步列表(见 pages/favorites.ts)。
 */

import { ApiError, api } from '../lib/api'
import { toast } from './toast'
import type { WordCard } from '../types/api'

export const WORD_FAVORITE_CHANGED = 'word-favorite-changed'

export interface WordFavoriteChangedDetail {
  wordId: number
  saved: boolean
}

export interface WordCardStore {
  open: boolean
  loading: boolean
  card: WordCard | null
  error: string
  /** 当前单词是否已收藏 */
  saved: boolean
  toggling: boolean
  show(id: number): Promise<void>
  close(): void
  trapFocus(event: KeyboardEvent): void
  toggleSave(): Promise<void>
  playAudio(url: string, fallbackUrl: string): void
}

export function wordCardStore(): WordCardStore {
  // 播放中的音频放在闭包里, 避免被 Alpine 包成响应式代理
  let audio: HTMLAudioElement | null = null
  let request = 0
  let trigger: HTMLElement | null = null

  const stopAudio = (): void => {
    audio?.pause()
    audio = null
  }

  /** 浏览器朗读兜底(在线音频失败时用) */
  const speak = (word: string): void => {
    if (!word || typeof speechSynthesis === 'undefined') return
    const utterance = new SpeechSynthesisUtterance(word)
    utterance.lang = 'en-US'
    speechSynthesis.cancel()
    speechSynthesis.speak(utterance)
  }

  return {
    open: false,
    loading: false,
    card: null,
    error: '',
    saved: false,
    toggling: false,

    async show(id: number) {
      const current = ++request
      trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null
      stopAudio()
      this.open = true
      this.card = null
      this.error = ''
      this.saved = false
      this.toggling = false
      this.loading = true
      requestAnimationFrame(() => {
        if (current === request && this.open) document.querySelector<HTMLButtonElement>('[data-word-card-close]')?.focus()
      })
      try {
        const [card, savedIds] = await Promise.all([
          api.words.get(id),
          api.favorites.savedIds('word').catch(() => [] as number[]),
        ])
        if (current !== request) return
        this.card = card
        this.saved = savedIds.includes(id)
      } catch (error) {
        if (current === request) this.error = error instanceof ApiError ? error.message : '词卡加载失败'
      } finally {
        if (current === request) this.loading = false
      }
    },

    close() {
      ++request
      stopAudio()
      this.loading = false
      this.toggling = false
      this.open = false
      this.card = null
      this.error = ''
      if (trigger?.isConnected) trigger.focus()
      trigger = null
    },

    /** 将焦点留在共用词卡内, 手机/Pad/PC 使用同一组键盘交互. */
    trapFocus(event) {
      const dialog = event.currentTarget as HTMLElement
      const buttons = [...dialog.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')].filter(button => button.getClientRects().length > 0)
      const first = buttons[0]
      const last = buttons[buttons.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    },

    async toggleSave() {
      const card = this.card
      if (!card || this.toggling) return
      const current = request
      this.toggling = true
      const wasSaved = this.saved
      const nextSaved = !wasSaved
      this.saved = nextSaved
      try {
        if (wasSaved) {
          await api.favorites.remove('word', card.id)
        } else {
          await api.favorites.add('word', card.id)
        }
        toast(wasSaved ? '已取消收藏' : '已收藏')
        window.dispatchEvent(
          new CustomEvent<WordFavoriteChangedDetail>(WORD_FAVORITE_CHANGED, {
            detail: { wordId: card.id, saved: nextSaved },
          }),
        )
      } catch (error) {
        if (current === request) this.saved = wasSaved
        toast(error instanceof ApiError ? error.message : '操作失败, 请重试')
      } finally {
        if (current === request) this.toggling = false
      }
    },

    playAudio(url: string, fallbackUrl: string) {
      const word = this.card?.headword ?? ''
      stopAudio()
      let usedFallback = false
      const play = (source: string): void => {
        const element = new Audio(source)
        audio = element
        const retryFallback = (): void => {
          if (audio !== element) return
          if (!usedFallback && fallbackUrl && fallbackUrl !== source) {
            usedFallback = true
            stopAudio()
            play(fallbackUrl)
          } else {
            speak(word)
          }
        }
        element.addEventListener('error', retryFallback, { once: true })
        void element.play().catch(retryFallback)
      }
      if (url) {
        play(url)
        return
      }
      if (fallbackUrl) {
        usedFallback = true
        play(fallbackUrl)
        return
      }
      speak(word)
    },
  }
}
